import {useLayoutEffect, useRef} from 'react'
import PropTypes from 'prop-types'

import EnrichQuestionCard from './EnrichQuestionCard'
import {INTERVIEW_PHASE, ITEM_STATUS} from './hooks/useEnrichInterview'
import {SparkleIcon} from './icons'

export const BANNERS = {
  loading: 'Reading your story the way your family would\u2026',
  writing: 'Writing your story with everything you shared\u2026',
  resultMarked: {lead: 'Here\u2019s your story with your answers woven in.', rest: ' New details are in green.'},
  resultPlain: {lead: 'Here\u2019s your finished story,', rest: ' written with everything you shared. Read it over and make sure it sounds like you.'}
}

// Space between a highlighted phrase and the card under it, and between the
// story's end and a free-floating card.
const CARD_GAP = 12
const FREE_CARD_GAP = 4
// The card's arrow sits over the start of the highlight, kept inside the card.
const ARROW_MIN = 18
const ARROW_INSET = 30
const ARROW_OFFSET = 14

/** Ranges in reading order, dropping any that starts inside an earlier one. */
const withoutOverlaps = ranges => {
  let at = 0

  return [...ranges].sort((a, b) => a.start - b.start).filter(range => {
    if (range.start < at) return false

    at = range.end

    return true
  })
}

/**
 * The story with each quoted phrase marked: `[{start, end, k}]` in reading
 * order, dropping a quote that overlaps an earlier one (prototype `famRender`).
 */
export const quoteRanges = (text, items) => {
  const lowered = text.toLowerCase()
  const ranges = (items || []).flatMap((item, k) => {
    const start = item.quote ? lowered.indexOf(item.quote.toLowerCase()) : -1

    return start > -1 ? [{start, end: start + item.quote.length, k}] : []
  })

  return withoutOverlaps(ranges)
}

/** The woven story with each new detail marked: `[{start, end}]` in order (prototype `famMarkBits`). */
export const markRanges = (text, marks) => {
  const ranges = (marks || []).flatMap(mark => {
    const start = mark ? text.indexOf(mark) : -1

    return start > -1 ? [{start, end: start + mark.length}] : []
  })

  return withoutOverlaps(ranges)
}

const markStatus = item => {
  if (item.answer.trim()) return 'answered'
  if (item.status === ITEM_STATUS.SKIPPED) return 'skipped'

  return ''
}

/**
 * The desktop Enrich interview inside the answer card (TK-3204, prototype
 * `fam-wrap`): a banner for the phases that need one, the story read-only
 * with the phrases the questions build on highlighted (click or Enter/Space
 * opens that question), the question card positioned under the open
 * highlight (or floating under the story for a question without one), and
 * finally the woven story with its new details in green.
 */
const EnrichInterviewView = ({interview, text}) => {
  const {phase, items, active, activeItem, woven, marks} = interview
  const wrapRef = useRef(null)
  const viewRef = useRef(null)
  const cardRef = useRef(null)

  const asking = phase === INTERVIEW_PHASE.ASK || phase === INTERVIEW_PHASE.LOADING || phase === INTERVIEW_PHASE.ERROR
  const showCard = phase === INTERVIEW_PHASE.ASK && Boolean(activeItem)
  // A question is anchored only when its phrase is actually marked: a quote
  // that isn't in the story, or overlaps an earlier one, has no highlight to
  // sit under, so its card floats like a question without a quote.
  const ranges = asking ? quoteRanges(text, items) : []
  const anchored = showCard && ranges.some(range => range.k === active)

  // The card sits under the open highlight, growing the wrapper so it never
  // covers the footer, and is scrolled into view when it lands off screen.
  useLayoutEffect(() => {
    const wrap = wrapRef.current
    const view = viewRef.current
    const card = cardRef.current

    if (!wrap || !view) return undefined

    const place = () => {
      wrap.style.paddingBottom = ''

      if (!card) return

      const mark = anchored ? view.querySelector(`.ls-fam-mark[data-k="${active}"]`) : null
      const top = mark ? mark.offsetTop + mark.offsetHeight + CARD_GAP : view.offsetTop + view.offsetHeight + FREE_CARD_GAP

      card.style.top = `${top}px`

      if (mark) {
        const arrow = Math.max(ARROW_MIN, Math.min(mark.offsetLeft + ARROW_OFFSET - card.offsetLeft, card.offsetWidth - ARROW_INSET))

        card.style.setProperty('--ax', `${arrow}px`)
      }

      const need = top + card.offsetHeight + 16 - view.offsetTop - view.offsetHeight

      if (need > 0) wrap.style.paddingBottom = `${need}px`

      const rect = card.getBoundingClientRect()

      if ((rect.bottom > window.innerHeight - 12 || rect.top < 0) && typeof card.scrollIntoView === 'function')
        card.scrollIntoView({block: 'nearest', behavior: 'smooth'})
    }

    place()
    window.addEventListener('resize', place)

    return () => window.removeEventListener('resize', place)
  }, [showCard, anchored, active, items])

  const handleMarkKeyDown = (event, k) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      interview.open(k)
    }
  }

  const renderMarked = () => {
    const parts = []
    let at = 0

    ranges.forEach(range => {
      const item = items[range.k]
      const segment = text.slice(range.start, range.end)
      const status = markStatus(item)
      const on = active === range.k

      parts.push(text.slice(at, range.start))
      parts.push(
        <mark
          aria-expanded={on}
          aria-label={`Your family asks about: ${segment}${status ? `, ${status}` : ''}`}
          className={`ls-fam-mark${status ? ` ${status}` : ''}${on ? ' on' : ''}`}
          data-k={range.k}
          key={range.k}
          role='button'
          style={{animationDelay: `${range.k * 120}ms`}}
          tabIndex={0}
          onClick={() => interview.open(range.k)}
          onKeyDown={event => handleMarkKeyDown(event, range.k)}>
          {segment}
        </mark>
      )
      at = range.end
    })
    parts.push(text.slice(at))

    return parts
  }

  const renderWoven = () => {
    const ranges = markRanges(woven, marks)
    const parts = []
    let at = 0

    ranges.forEach((range, i) => {
      parts.push(woven.slice(at, range.start))
      parts.push(<ins key={i}>{woven.slice(range.start, range.end)}</ins>)
      at = range.end
    })
    parts.push(woven.slice(at))

    return parts
  }

  const renderBanner = () => {
    if (phase === INTERVIEW_PHASE.LOADING) return <><span className='ls-spin' /> {BANNERS.loading}</>
    if (phase === INTERVIEW_PHASE.WRITING) return <><span className='ls-spin' /> {BANNERS.writing}</>

    if (phase === INTERVIEW_PHASE.RESULT) {
      const copy = marks.length ? BANNERS.resultMarked : BANNERS.resultPlain

      return <><SparkleIcon size={16} /><span><b>{copy.lead}</b>{copy.rest}</span></>
    }

    return null
  }

  const banner = renderBanner()

  return (
    <div className='ls-fam-wrap' ref={wrapRef}>
      {banner && <div className='ls-fam-banner' role='status'>{banner}</div>}
      <div aria-label='Your story, with parts your family might ask about' className='ls-diff-view ls-fam-view' ref={viewRef}>
        {asking && renderMarked()}
        {phase === INTERVIEW_PHASE.WRITING && text}
        {phase === INTERVIEW_PHASE.RESULT && renderWoven()}
      </div>
      {
        showCard &&
          <EnrichQuestionCard
            count={items.length}
            free={!anchored}
            goDisabled={interview.goDisabled}
            goLabel={interview.goLabel}
            goPrimary={interview.goPrimary}
            index={active}
            item={activeItem}
            ref={cardRef}
            onAnswer={interview.setAnswer}
            onExample={interview.addExample}
            onGo={interview.go}
            onJump={interview.jump}
          />
      }
    </div>
  )
}

EnrichInterviewView.propTypes = {
  interview: PropTypes.shape({
    active: PropTypes.number.isRequired,
    activeItem: PropTypes.object,
    addExample: PropTypes.func.isRequired,
    go: PropTypes.func.isRequired,
    goDisabled: PropTypes.bool,
    goLabel: PropTypes.string,
    goPrimary: PropTypes.bool,
    items: PropTypes.array,
    jump: PropTypes.func.isRequired,
    marks: PropTypes.arrayOf(PropTypes.string).isRequired,
    open: PropTypes.func.isRequired,
    phase: PropTypes.string.isRequired,
    setAnswer: PropTypes.func.isRequired,
    woven: PropTypes.string.isRequired
  }).isRequired,
  text: PropTypes.string.isRequired
}

export default EnrichInterviewView
